import {
  Attachment,
  Chat,
  CUSTOMER_MESSAGE_SUBTYPES,
  CustomerMessageSubtype,
  InitChatProps,
  PersistentMessage,
} from "@/types/chat"
import { ExploreLocation } from "@/types/explore"
import { SimpleHttpError } from "@/types/http"
import { Trip } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { buildClientAxios } from "@/lib/client-axios"
import { isMicrositeHost } from "@/lib/is-embedded"
import { chatsApi } from "@/lib/storage/api/chats-api"
import {
  HighlightedMessage,
  selectFilteredPlaceType,
  selectHasHighlightedMessage,
  selectHighlightedPlaces,
  setExpandedMap,
  setFilteredPlaces,
  setFilteredPlaceType,
  setHighlightedMessage,
  setShowSmallUIMap,
} from "@/lib/storage/chat-mode-slice"
import { closeFlightSheet } from "@/lib/storage/flight-search-slice"
import { selectLocale } from "@/lib/storage/locale-selectors"
import { isChatPage } from "@/lib/utils/page"
import { cleanUrlParams } from "@/lib/utils/routing"
import { singleParam } from "@/lib/utils/url"
import {
  chatToAmplitudeProperties,
  placeToAmplitudeProperties,
} from "../analytics/amplitude-properties"
import {
  AmplitudeInitChatProps,
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "../analytics/amplitude-utils"
import { ChatApi } from "../chat-api"
import { StreamedMessageListener } from "../streamed-message-listener"
import { itineraryApi } from "./api/itinerary-api"
import { selectDisplayPosition } from "./geolocation-selectors"
import { selectAddedPlaces } from "./itinerary-slice"
import { AppDispatch, RootState } from "./redux-store"
import userSlice, {
  selectChatId,
  selectChatMemoryEnabled,
  selectFirstLoadedMessageId,
  selectHasPersistentMessages,
  selectIsCustomerMessagePending,
  selectIsGroupChat,
  selectIsLoadingMessages,
  selectLastLoadedMessageId,
  selectServerHasOlderMessages,
} from "./user-slice"

import "@/lib/storage/message-thunks"

import { NextRouter } from "next/router"
import { DayRange } from "@/types/date"
import { Place } from "@/types/place"
import { TravelerGroup, TripRef } from "@/types/trip"
import nProgress from "nprogress"

import { isLocation } from "@/lib/entity-utils"
import { links, routes } from "@/lib/routes"
import {
  markChatTabEngaged,
  markChatTabEngagedFromChat,
  markChatTabEngagedFromState,
  markCompletedVoiceChat,
} from "@/lib/storage/chat-tab-outside-nav"
import { sendMessage } from "@/lib/storage/message-thunks"
import {
  setBurgerOpen,
  setChatListDialogOpen,
  setNavValue,
} from "@/lib/storage/site-nav-slice"
import { clearChatsSearch } from "@/lib/storage/site-nav-thunks"
import {
  ChatBotVersionKey,
  ChatBotVersionKeys,
  selectChatBotVersion,
  setChatBotVersion,
} from "@/lib/storage/ui-settings-slice"
import { toastManager } from "@/lib/toast"
import { compact } from "@/lib/utils/collections"
import { AmplitudeAskAIPlaceType } from "../analytics/amplitude-types"
import { sameDay } from "../date-utils"
import { ExploreApi } from "../explore-api"
import {
  formatDayObjectAsMonthDay,
  formatTravelerSummary,
} from "../format-utils"
import { priceQuotesApi } from "./api/price-quotes-api"
import { populateTripCache, tripsApi } from "./api/trips-api"
import { selectEmbedded, selectIsSplitView, selectIsXS } from "./app-slice"
import { selectAllPlaces } from "./chat-selectors"
import { closeDetailSheet, closeDetailSheetIfTrip } from "./detail-sheet-slice"
import { selectIsEmbeddedChatView } from "./embedded-slice"
import { selectIsStreaming } from "./explore-slice"
import {
  selectTrip,
  selectTripIsUserCreated,
  selectTripRef,
  selectTripSavedPlaces,
} from "./trip-slice"
import { initTrip, openTripSheet } from "./trip-thunks"
import { selectVoiceModeOpen, voiceChatActions } from "./voice-chat-slice"

const clientAxios = buildClientAxios()
const chatApi = new ChatApi(clientAxios)

/**
 * Replace the route with /chat/:chatId, preserving query params
 */
async function replaceChatRoute(router: NextRouter, chatId: number) {
  await router.replace(
    {
      pathname: router.pathname,
      query: { ...router.query, id: String(chatId) },
    },
    undefined,
    { shallow: true }
  )
}

function customerMessageSubTypeFromQuery(
  value: string | undefined
): CustomerMessageSubtype | undefined {
  if (
    value &&
    CUSTOMER_MESSAGE_SUBTYPES.includes(value as CustomerMessageSubtype)
  ) {
    return value as CustomerMessageSubtype
  }
}

export const initChatAmplitude = createAsyncThunk<void, Chat, {}>(
  "chatThunks/initChat",
  async (chat) => {
    // IMPORTANT: This needs to be run first to set up properties for chat and trip before other events are fired
    // Especially for GTM
    const chatId = chat.id
    let analyticsChatProperties: AmplitudeInitChatProps = {
      type: "chatProps",
      chat: {
        id: chatId,
        ...(chat.vertical ? { vertical: chat.vertical } : {}),
      },
    }
    const tripId = chat.trip.id
    const tripName = chat.trip.name
    if (tripName && tripId) {
      analyticsChatProperties = {
        ...analyticsChatProperties,
        trip: { id: tripId, name: tripName },
      }
    }
    setAnalyticsUserProperties(analyticsChatProperties)
  }
)

export const initChat = createAsyncThunk<
  void,
  InitChatProps,
  { dispatch: AppDispatch; state: RootState }
>("chatThunks/initChat", async (initProps, { dispatch, getState }) => {
  void dispatch(initChatAmplitude(initProps.initChat))
  void dispatch(closeDetailSheet())
  dispatch(setFilteredPlaceType(undefined))
  void dispatch(handleHighlightedMessage(undefined))
  void dispatch(updateFilteredPlaces())
  dispatch(setShowSmallUIMap(false))
  dispatch(setExpandedMap(false))

  dispatch(userSlice.actions.setChat(initProps.initChat))

  void dispatch(initTrip(initProps.initChat.trip))

  // Eagerly load all quotes for this chat so they're available in the RTK cache
  void dispatch(
    priceQuotesApi.endpoints.getChatQuotes.initiate({
      chatId: initProps.initChat.id,
    })
  )

  // Only set currentLocation from SSR if we don't have geolocation data
  // (geolocation is more accurate than IP-derived location)
  if (initProps.userLocation && !selectDisplayPosition(getState())) {
    dispatch(userSlice.actions.setCurrentLocation(initProps.userLocation))
  }

  if (initProps.openTripSheet === true) {
    void dispatch(openTripSheet())
  } else if (initProps.openTripSheet === false) {
    void dispatch(closeDetailSheetIfTrip())
  }

  sendAnalyticsEvent({
    name: initProps.initChat.group ? "ViewChatGroupPage" : "ViewChatPage",
    properties: { chat: { id: initProps.initChat.id } },
  })
})

export const initChatPage = createAsyncThunk<
  void,
  { router: NextRouter },
  { dispatch: AppDispatch; state: RootState }
>("chatThunks/initChatPage", async ({ router }, { dispatch, getState }) => {
  const queryId = Array.isArray(router.query.id)
    ? router.query.id[0]
    : router.query.id
  const parsedId = queryId && queryId !== "new" ? parseInt(queryId) : NaN
  const numericChatId = !isNaN(parsedId) ? parsedId : undefined
  const isBareChat =
    queryId === undefined ||
    queryId === "" ||
    (queryId !== "new" && numericChatId === undefined)
  const isXS = selectIsXS(getState())
  const embedded = selectEmbedded(getState())
  const prompt = singleParam(router.query.q)
  const hint = singleParam(router.query.hint)
  const subType = customerMessageSubTypeFromQuery(
    singleParam(router.query.sub_type)
  )
  const entityRefsParam = singleParam(router.query.entity_refs)
  const entityRefs = entityRefsParam
    ?.split(",")
    .map((r) => r.trim())
    .filter(Boolean)
  const botParam = singleParam(router.query.bot)

  // If ?bot= param is specified and valid, set it before creating chat
  if (botParam && ChatBotVersionKeys.includes(botParam as ChatBotVersionKey)) {
    dispatch(setChatBotVersion(botParam as ChatBotVersionKey))
  }

  const currentChatId = selectChatId(getState())
  const conversationLoaded =
    currentChatId > 0 &&
    (selectHasPersistentMessages(getState()) ||
      selectIsCustomerMessagePending(getState()))

  const skipChatLoad =
    (numericChatId !== undefined && currentChatId === numericChatId) ||
    (numericChatId === undefined && conversationLoaded)

  nProgress.start()
  try {
    // Fetch user location if we don't have geolocation data
    const hasGeoPosition = !!selectDisplayPosition(getState())
    const locationPromise = hasGeoPosition
      ? Promise.resolve(undefined)
      : new ExploreApi(buildClientAxios()).fetchUserLocation()
    if (!skipChatLoad) {
      if (queryId === "new") {
        const { chatId: newChatId } = await dispatch(createChat()).unwrap()
        await replaceChatRoute(router, newChatId)
      } else if (numericChatId !== undefined) {
        try {
          await dispatch(fetchChatAndInitializeApp(numericChatId)).unwrap()
        } catch (error) {
          if (
            error instanceof SimpleHttpError &&
            error.response?.status === 404
          ) {
            const { chatId: newChatId } = await dispatch(createChat()).unwrap()
            await replaceChatRoute(router, newChatId)
          } else {
            throw error
          }
        }
      } else if (isBareChat) {
        if (isXS && embedded) {
          await dispatch(fetchChatAndInitializeApp("latest")).unwrap()
        } else {
          const { chatId: newChatId } = await dispatch(createChat()).unwrap()
          await replaceChatRoute(router, newChatId)
        }
      }
    }

    const applyLocation = (location: ExploreLocation | undefined) => {
      if (location) {
        dispatch(userSlice.actions.setCurrentLocation(location))
      }
    }

    if (prompt) {
      applyLocation(await locationPromise)
    } else {
      void locationPromise.then(applyLocation, () => undefined)
    }

    if (router.query.tripSheet === "1" && selectTripIsUserCreated(getState())) {
      void dispatch(openTripSheet())
    }

    if (prompt) {
      void dispatch(
        sendMessage({
          messageString: prompt,
          hint,
          entityRefs,
          subType,
        })
      )
    }

    const consumedParams = ["q", "hint", "entity_refs", "sub_type"]
    if (consumedParams.some((p) => router.query[p])) {
      const routeChatId = selectChatId(getState())
      cleanUrlParams(router, consumedParams, {
        routeChatId: routeChatId > 0 ? routeChatId : undefined,
      })
    }
  } finally {
    nProgress.done()
  }
})

export const goToChat = createAsyncThunk<
  { chatId: number } | undefined,
  {
    router: NextRouter
    chatId: number | "latest"
    tripSheetEventId?: number
    tripSheet?: boolean
    skipRouteCheck?: boolean
    skipFetch?: boolean
    utmContent?: string
  }, // chatId
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/GoToChat",
  async (
    {
      router,
      chatId: targetChatId,
      tripSheetEventId,
      tripSheet,
      skipRouteCheck,
      skipFetch = false,
      utmContent,
    },
    { dispatch, getState }
  ) => {
    dispatch(setBurgerOpen(false))
    dispatch(setChatListDialogOpen(false))
    void dispatch(clearChatsSearch())

    const isEmbeddedChatView = selectIsEmbeddedChatView(getState())
    const isChat = isChatPage(router) || isEmbeddedChatView

    if (!skipRouteCheck && !isChat) {
      const query = new URLSearchParams(
        compact({
          tripSheet: tripSheetEventId || tripSheet ? "1" : undefined,
          utm_content: utmContent,
        })
      )
      await router.push(routes.chat.href({ id: [String(targetChatId)], query }))
      return
    }

    nProgress.start()

    try {
      const currentChatId = selectChatId(getState())
      const willFetch =
        !skipFetch &&
        (targetChatId === "latest" || currentChatId !== targetChatId)

      let resolvedChatId: number
      // if we are on the chat page, handle it all client side
      if (willFetch) {
        // Close overlays before the fetch so that when fetchChatAndInitializeApp
        // changes the chatId, handleReduxStateChange won't carry the old entity's
        // ref param into the new chat's URL (which would re-open the sheet).
        void dispatch(closeDetailSheet())
        void dispatch(closeFlightSheet())
        void dispatch(handleHighlightedMessage(undefined))
        const res = await dispatch(
          fetchChatAndInitializeApp(targetChatId)
        ).unwrap()
        resolvedChatId = res.chatId
      } else {
        resolvedChatId =
          targetChatId === "latest" ? currentChatId : targetChatId
      }

      if (tripSheet || tripSheetEventId) {
        void dispatch(openTripSheet({ eventId: tripSheetEventId }))
      } else {
        void dispatch(closeDetailSheetIfTrip())
      }

      // update url on microsite when selecting chat from site-nav;
      // handleChatMenuItemClick calls goToChat directly (not via handleRouteChange)
      if (isMicrositeHost()) {
        window.history.replaceState(null, "", links.chat(resolvedChatId))
      }

      markChatTabEngagedFromState(dispatch, getState)

      return { chatId: resolvedChatId }
    } finally {
      nProgress.done()
    }
  }
)

export const fetchChatAndInitializeApp = createAsyncThunk<
  { chatId: number },
  number | "latest", // chatId
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: SimpleHttpError
  }
>(
  "chatThunks/fetchChatAndInitializeApp",
  async (chatId, { dispatch, getState, rejectWithValue }) => {
    const currentTripRef = selectTripRef(getState())
    let chat: Chat
    try {
      chat = await chatApi.fetchChat(chatId)
    } catch (error) {
      if (error instanceof SimpleHttpError) {
        return rejectWithValue(error)
      }
      throw error
    }

    void dispatch(initChatAmplitude(chat))

    void populateTripCache(dispatch, chat.trip)

    dispatch(userSlice.actions.setChat(chat))

    markChatTabEngagedFromChat(dispatch, chat)

    if (chat.trip.ref !== currentTripRef) {
      void Promise.all([
        chat.trip.user_created
          ? dispatch(
              itineraryApi.endpoints.getItinerary.initiate({
                tripRef: chat.trip.ref,
              })
            )
          : Promise.resolve(undefined),
        chat.trip.collaboration
          ? dispatch(
              tripsApi.endpoints.getCollaborators.initiate(chat.trip.ref)
            )
          : Promise.resolve(undefined),
      ])
    }

    return { chatId: chat.id }
  }
)

export const moveChatToTrip = createAsyncThunk<
  void,
  { chatId: number; tripRef: TripRef },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/moveChatToTrip",
  async ({ chatId, tripRef }, { dispatch, getState }) => {
    const response = await chatApi.moveChatToTrip(chatId, tripRef)
    const currentChatId = selectChatId(getState())
    const newTripRef = response.chat.trip.ref

    if (chatId === currentChatId) {
      dispatch(userSlice.actions.setChat(response.chat))
      loadChatTrip(dispatch, response.chat.trip)
    }

    dispatch(chatsApi.util.invalidateTags(["Chats"]))
    dispatch(
      tripsApi.util.invalidateTags([{ type: "TripCounts", id: newTripRef }])
    )
  }
)

export function loadChatTrip(dispatch: AppDispatch, trip: Trip) {
  void populateTripCache(dispatch, trip)
  void Promise.all([
    dispatch(
      itineraryApi.endpoints.getItinerary.initiate(
        { tripRef: trip.ref },
        { forceRefetch: true }
      )
    ),
    trip.collaboration
      ? dispatch(tripsApi.endpoints.getCollaborators.initiate(trip.ref))
      : Promise.resolve(undefined),
  ])
}

// Toggle long-term memory for the current chat. Optimistically flips the flag, then
// reconciles with the server (which also reports whether the setting is still editable).
export const setChatMemory = createAsyncThunk<
  void,
  { chatId: number; memoryEnabled: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/setChatMemory",
  async ({ chatId, memoryEnabled }, { dispatch, getState }) => {
    if (selectChatId(getState()) !== chatId) return
    const previous = selectChatMemoryEnabled(getState())

    dispatch(
      userSlice.actions.updateChat({
        id: chatId,
        memory_enabled: memoryEnabled,
      })
    )

    try {
      const response = await chatApi.setChatMemory(chatId, memoryEnabled)
      if (selectChatId(getState()) === chatId) {
        dispatch(
          userSlice.actions.updateChat({
            id: chatId,
            memory_enabled: response.chat.memory_enabled,
            memory_setting_editable: response.chat.memory_setting_editable,
          })
        )
      }
    } catch (e) {
      if (selectChatId(getState()) === chatId) {
        dispatch(
          userSlice.actions.updateChat({ id: chatId, memory_enabled: previous })
        )
      }
      throw e
    }
  }
)

export const removeChatFromTrip = createAsyncThunk<
  void,
  { chatId: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/removeChatFromTrip",
  async ({ chatId }, { dispatch, getState }) => {
    const response = await chatApi.removeChatFromTrip(chatId)
    const currentChatId = selectChatId(getState())

    if (chatId === currentChatId) {
      void populateTripCache(dispatch, response.chat.trip)
      dispatch(userSlice.actions.setChat(response.chat))

      const newTripRef = response.chat.trip.ref
      void dispatch(
        itineraryApi.endpoints.getItinerary.initiate(
          { tripRef: newTripRef },
          { forceRefetch: true }
        )
      )
    }

    dispatch(chatsApi.util.invalidateTags(["Chats"]))
  }
)

export const createChat = createAsyncThunk<
  { chatId: number; tripRef: TripRef },
  | {
      message?: PersistentMessage
      tripRef?: TripRef
      kickoffContext?: string
      entityRefs?: string[]
      /** A start_server_chat pill: the server makes the chat and its opening. */
      actionPillRef?: string
    }
  | undefined,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("chatThunks/createChat", async (props, { dispatch, getState }) => {
  const currentTripRef = selectTripRef(getState())
  const { message, tripRef, kickoffContext, entityRefs, actionPillRef } =
    props || {}

  let response
  if (message) {
    response = await chatApi.createChatWithMessage(message.id)
  } else if (actionPillRef) {
    response = await chatApi.startServerChat(actionPillRef)
  } else {
    response = await chatApi.createChat(undefined, tripRef, undefined, {
      kickoffContext,
      entityRefs,
    })
  }
  void populateTripCache(dispatch, response.chat.trip)
  dispatch(userSlice.actions.setChat(response.chat))
  void dispatch(initChatAmplitude(response.chat))
  dispatch(chatsApi.util.invalidateTags(["Chats"]))
  dispatch(
    tripsApi.util.invalidateTags([
      { type: "TripCounts", id: response.chat.trip.ref },
    ])
  )

  if (tripRef && tripRef !== currentTripRef) {
    void Promise.all([
      response.chat.trip.user_created
        ? dispatch(itineraryApi.endpoints.getItinerary.initiate({ tripRef }))
        : Promise.resolve(undefined),
      response.chat.trip.collaboration
        ? dispatch(tripsApi.endpoints.getCollaborators.initiate(tripRef))
        : Promise.resolve(undefined),
    ])
  }

  sendAnalyticsEvent({
    name: "ChatCreate",
    properties: chatToAmplitudeProperties(response.chat),
  })

  if (message || kickoffContext || actionPillRef) {
    markChatTabEngaged(dispatch, response.chat.id)
  } else {
    markChatTabEngagedFromChat(dispatch, response.chat)
  }

  return { chatId: response.chat.id, tripRef: response.chat.trip.ref }
})

export const createChatWithKickoffAndGoToChat = createAsyncThunk<
  { chatId: number },
  (
    | { kickoffContext: string; entityRefs?: string[]; tripRef?: TripRef }
    | { actionPillRef: string }
  ) & { router: NextRouter },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/createChatWithKickoffAndGoToChat",
  async ({ router, ...opening }, { dispatch, getState }) => {
    if (selectVoiceModeOpen(getState())) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "chat_started" }))
    }

    try {
      const result = await dispatch(createChat(opening)).unwrap()
      await dispatch(
        goToChat({ chatId: result.chatId, router, skipFetch: true })
      ).unwrap()
      return { chatId: result.chatId }
    } catch (error) {
      const msg = "There was a problem starting a new chat. Please try again."
      console.error(msg, error)
      toastManager.add({ type: "error", description: msg })
      throw error
    }
  }
)

export const createChatInTrip = createAsyncThunk<
  void,
  { tripRef: TripRef; router: NextRouter },
  { dispatch: AppDispatch }
>(
  "siteNavThunks/createChatInTrip",
  async ({ tripRef, router }, { dispatch }) => {
    try {
      const result = await dispatch(createChat({ tripRef })).unwrap()
      dispatch(setNavValue(""))
      void dispatch(goToChat({ chatId: result.chatId, router }))
    } catch (error) {
      const msg =
        "There was a problem creating a new chat for this trip. Please try again."
      console.error(msg, error)
      toastManager.add({ type: "error", description: msg })
    }
  }
)

// The user hung up: end the call right where they are — ending a voice call
// never navigates. A call they spoke in becomes the mobile Chat tab's resume
// target; a silent one persisted nothing and leaves no mark either (see
// markCompletedVoiceChat).
export const hangUpVoiceMode = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("chatThunks/hangUpVoiceMode", async (_, { dispatch, getState }) => {
  const state = getState()
  dispatch(voiceChatActions.voiceModeClosed({ reason: "user_ended" }))
  markCompletedVoiceChat(dispatch, state)
})

export const createChatAndSendMessage = createAsyncThunk<
  { chatId: number },
  {
    message: string
    subType?: CustomerMessageSubtype
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "chatThunks/createChat",
  async ({ message, subType }, { dispatch, getState }) => {
    const { chatId } = await dispatch(createChat()).unwrap()
    const chatBotVersion = selectChatBotVersion(getState())

    await chatApi.addCustomerMessage(chatId, message, {
      bot: chatBotVersion,
      subType,
    })
    markChatTabEngaged(dispatch, chatId)

    return { chatId }
  }
)

// Opens a fresh chat seeded with a customer message and navigates to it. Uses
// the sendMessage thunk (optimistic message + streamed bot reply) and goToChat
// so the new chat renders populated rather than blank. Pass tripRef to put the
// chat in an existing trip; omit it for a brand-new trip.
export const createChatAndSendMessageAndGoToChat = createAsyncThunk<
  { chatId: number },
  {
    tripRef?: TripRef
    messageString: string
    hint?: string
    attachments?: Attachment[]
    subType?: CustomerMessageSubtype
    entityRefs?: string[]
    incognito?: boolean
    router: NextRouter
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/createChatAndSendMessageAndGoToChat",
  async (
    {
      tripRef,
      messageString,
      hint,
      attachments,
      subType,
      entityRefs,
      incognito,
      router,
    },
    { dispatch, getState }
  ) => {
    // Hang-up-first rule (see sendOrEditMessage). The chat switch below would
    // end the call anyway via the middleware's setChat listener, but only
    // after the createChat round trip — hang up at the click instead.
    if (selectVoiceModeOpen(getState())) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "chat_started" }))
    }

    try {
      const result = await dispatch(createChat({ tripRef })).unwrap()
      // Before the first message, while the chat's memory setting can change
      if (incognito) {
        await dispatch(
          setChatMemory({ chatId: result.chatId, memoryEnabled: false })
        ).unwrap()
      }
      await dispatch(
        sendMessage({ messageString, hint, attachments, subType, entityRefs })
      ).unwrap()
      const isEmbeddedChatView = selectIsEmbeddedChatView(getState())
      const onChatPage = isChatPage(router) || isEmbeddedChatView
      if (!onChatPage) {
        await router.push(links.chat(result.chatId))
      } else {
        await dispatch(
          goToChat({ chatId: result.chatId, router, skipFetch: true })
        ).unwrap()
      }
      return { chatId: result.chatId }
    } catch (error) {
      const msg = "There was a problem starting a new chat. Please try again."
      console.error(msg, error)
      toastManager.add({ type: "error", description: msg })
      throw error
    }
  }
)

export const fetchNewTripLogItems = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("chatThunks/fetchNewTripLogItems", async (_args, { dispatch, getState }) => {
  const isGroupChat = selectIsGroupChat(getState())
  if (isGroupChat) {
    // We show trip log items inline in a group chat,
    // and we load new ones by fetching messages (which
    // returns new messages and log items).
    await dispatch(fetchNewMessages())
  }
})

export const fetchNewMessages = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("chatThunks/fetchNewMessages", async (_args, { dispatch, getState }) => {
  if (selectIsLoadingMessages(getState())) {
    return
  }
  if (selectIsStreaming(getState())) {
    return
  }

  const chatId = selectChatId(getState())
  const isGroupChat = selectIsGroupChat(getState())
  const chatApi = new ChatApi(buildClientAxios())

  // TODO: might need to be smarter about this if there are
  // more than N messages in the chat that we haven't loaded
  // (since the fetch will only give us N).
  // TOOD: also to consider: afterId is giving us the latest, not the next N
  const afterId = selectLastLoadedMessageId(getState())

  if (afterId === undefined && isGroupChat) {
    // Kick off a request for the group chat recap in parallel to the
    // message fetch.
    void chatApi
      .fetchRecap(chatId)
      .then((recap) => {
        if (recap) {
          dispatch(
            userSlice.actions.setRecapMessage({
              id: recap.timestamp.epoch_millis,
              type: "recap",
              body: recap.body,
              timestamp: recap.timestamp,
              markers: [],
              places: [],
              inspirations: [],
            })
          )
        }
      })
      .catch((error) => console.error("Error generating summary", error))
  }

  dispatch(userSlice.actions.setMessageLoadingStatus("loading-new"))
  try {
    const response = await chatApi.fetchMessages(chatId, { afterId })
    if (chatId === selectChatId(getState())) {
      // If we're don't specify afterId, then we're getting the latest
      // messages, and we know the server's "more" response tells us
      // whether there are older messages. If we do specify afterId,
      // then "more" tells us whether there are newer messages, so we
      // don't know about older.
      const serverHasOlderMessages =
        afterId === undefined ? response.more : undefined
      dispatch(
        userSlice.actions.addOrUpdateMessages({
          messages: response.messages,
          logItems: response.log_items,
          serverHasOlderMessages,
        })
      )
    }
  } catch (error) {
    dispatch(userSlice.actions.setMessageLoadingStatus("error"))
    throw error
  }
  dispatch(userSlice.actions.setMessageLoadingStatus("idle"))
})

export const fetchOldMessages = createAsyncThunk<
  boolean,
  HTMLElement,
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/fetchOldMessages",
  async (scrollContainer, { dispatch, getState }) => {
    if (selectIsLoadingMessages(getState())) {
      return false
    }
    if (selectServerHasOlderMessages(getState()) === false) {
      return false
    }

    const chatId = selectChatId(getState())

    dispatch(userSlice.actions.setMessageLoadingStatus("loading-old"))
    try {
      const firstLoadedMessageId = selectFirstLoadedMessageId(getState())
      const response = await chatApi.fetchMessages(chatId, {
        beforeId: firstLoadedMessageId,
      })
      if (chatId === selectChatId(getState())) {
        dispatch(
          userSlice.actions.addOrUpdateMessages({
            messages: response.messages,
            logItems: response.log_items,
            serverHasOlderMessages: response.more,
          })
        )

        const scrollBottom =
          scrollContainer.scrollHeight -
          (scrollContainer.clientHeight + scrollContainer.scrollTop)

        // Let the DOM settle after the messages are added and then reset
        // the scroll position so that we maintain the same distance from
        // the *bottom* of the text messages.
        requestAnimationFrame(() => {
          scrollContainer.scrollTop =
            scrollContainer.scrollHeight -
            (scrollBottom + scrollContainer.clientHeight)

          // repaint again, seems to really help for Safari
          requestAnimationFrame(() => {
            scrollContainer.scrollTop =
              scrollContainer.scrollHeight -
              (scrollBottom + scrollContainer.clientHeight)
          })
        })
      }
    } catch (error) {
      dispatch(userSlice.actions.setMessageLoadingStatus("error"))
      throw error
    }
    dispatch(userSlice.actions.setMessageLoadingStatus("idle"))

    return false
  }
)

export const generatePlaceChat = createAsyncThunk<
  number,
  {
    place: Place
    range?: DayRange
    travelers?: TravelerGroup
    question?: string
    newChat?: boolean
    tripRef?: TripRef
    subType?: CustomerMessageSubtype
    amplitudeType: AmplitudeAskAIPlaceType
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/generatePlaceChat",
  async (
    {
      place,
      range,
      travelers,
      question,
      newChat = true,
      tripRef: explicitTripRef,
      subType,
      amplitudeType,
    },
    { dispatch, getState }
  ) => {
    // Hang-up-first rule, needed here too because the newChat path posts
    // directly instead of funneling through sendOrEditMessage (whose guard
    // covers newChat:false). Ending the call unmounts the overlay, revealing
    // the answer streaming into text chat.
    if (selectVoiceModeOpen(getState())) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "chat_started" }))
    }

    const trip = selectTrip(getState())
    const tripRef =
      trip?.user_created && (!explicitTripRef || explicitTripRef === trip.ref)
        ? trip.ref
        : undefined
    const message = question || `What can you tell me about ${place.name}?`

    const locationDetails = [
      place.city !== place.name ? place.city : null,
      place.state !== place.name ? place.state : null,
      place.region !== place.name ? place.region : null,
      place.country !== place.name ? place.country : null,
    ]
      .filter(Boolean)
      .join(", ")

    let hint = isLocation(place)
      ? `${place.name} is a location in ${locationDetails}. `
      : `${place.name} is a ${place.type} located in ${locationDetails}. `

    if (range?.from) {
      const locale = selectLocale(getState())
      if (range.to && !sameDay(range.from, range.to)) {
        hint += `Travel dates: ${formatDayObjectAsMonthDay(locale, range.from)} to ${formatDayObjectAsMonthDay(locale, range.to)}. `
      } else {
        hint += `Travel date: ${formatDayObjectAsMonthDay(locale, range.from)}. `
      }
    }

    if (travelers) {
      hint += `Travelers: ${formatTravelerSummary(travelers)}.`
    }

    let chatId: number
    if (newChat) {
      chatId = (
        await dispatch(
          createChat({
            tripRef,
          })
        ).unwrap()
      ).chatId
      const chatBotVersion = selectChatBotVersion(getState())

      const response = await chatApi.addCustomerMessage(chatId, message, {
        bot: chatBotVersion,
        hint,
        entityRef: place.entity_ref,
        subType,
      })
      if (response.redirect_to_chat_id) {
        chatId = response.redirect_to_chat_id
        await dispatch(fetchChatAndInitializeApp(chatId)).unwrap()
      } else {
        dispatch(
          userSlice.actions.addMessage({ message: response.message, chatId })
        )
        if (response.stream_id) {
          StreamedMessageListener.listenToStream(
            chatId,
            response.stream_id,
            dispatch,
            undefined
          )
        }
      }
    } else {
      chatId = selectChatId(getState())
      await dispatch(
        sendMessage({
          messageString: message,
          hint,
          entityRef: place.entity_ref,
          subType,
        })
      )
    }

    sendAnalyticsEvent({
      name: "AIPlaceAsk",
      properties: {
        ...placeToAmplitudeProperties(place),
        type: amplitudeType,
      },
    })

    return chatId
  }
)

export const handleHighlightedMessage = createAsyncThunk<
  void,
  HighlightedMessage,
  { dispatch: AppDispatch; state: RootState }
>(
  "chatThunks/handleHighlightedMessage",
  async (message, { dispatch, getState }) => {
    const isSplitView = selectIsSplitView(getState())
    dispatch(setHighlightedMessage(message))
    void dispatch(updateFilteredPlaces())
    if (message) {
      void dispatch(closeDetailSheet())
      if (!isSplitView) {
        dispatch(setShowSmallUIMap(true))
      }
    }
  }
)

export const updateFilteredPlaces = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("chatThunks/updateFilteredPlaces", (_, { getState, dispatch }) => {
  const state = getState()
  const allPlaces = selectAllPlaces(state)
  const savedPlaces = selectTripSavedPlaces(state)
  const hasHighlightedMessage = selectHasHighlightedMessage(state)
  const highlightedPlaces = selectHighlightedPlaces(state)
  const addedPlaces = selectAddedPlaces(state)
  const filteredPlaceType = selectFilteredPlaceType(state)

  let placesToFilter: Place[]
  if (hasHighlightedMessage) placesToFilter = highlightedPlaces
  else if (filteredPlaceType === "saved") placesToFilter = savedPlaces
  else if (filteredPlaceType === "added") placesToFilter = addedPlaces
  else placesToFilter = allPlaces

  const filteredPlaces =
    filteredPlaceType === "saved"
      ? savedPlaces
      : filteredPlaceType === "added"
        ? addedPlaces
        : placesToFilter.filter(
            (place) => !filteredPlaceType || place.type === filteredPlaceType
          )

  dispatch(setFilteredPlaces(filteredPlaces))
})
